import { createSlice } from '@reduxjs/toolkit';
import { apiSliceWithPrefix } from '../api/apiSlice';

// RTK Query API endpoints pour les phone
export const phoneApiSlice = apiSliceWithPrefix.injectEndpoints({
        endpoints: (builder) => ({
                getPhones: builder.query({
                        query: (id) => {
                                // Récupérer les informations utilisateur pour déterminer le scope
                                const userData = localStorage.getItem('user');
                                let isSupervisor = false;
                                
                                try {
                                        const parsedUser = JSON.parse(userData);
                                        isSupervisor = parsedUser?.role === 'SUPERVISOR';
                                } catch (error) {
                                        console.warn('Erreur lors du parsing des données utilisateur:', error);
                                }
                                
                                // Si superviseur, récupérer toutes les agences, sinon seulement l'agence de l'utilisateur
                                const queryParams = isSupervisor ? 'all_agencies=true' : '';
                                const url = `/phones?${queryParams}&_t=${Date.now()}`;
                                
                                
                                return { url };
                        },
                        providesTags: (result, error, id) => {
                                return result
                                        ? [
                                                ...result.map(({ id }) => ({ type: 'Phone', id })),
                                                { type: 'Phone', id: 'LIST' },
                                        ]
                                        : [{ type: 'Phone', id: 'LIST' }];
                        },
                        invalidatesTags: (result, error, id) => [{ type: 'Phone', id }],
                        keepUnusedDataFor: 0, // Ne pas garder les données en cache
                }),
                getPhoneById: builder.query({
                        query: (id) => `/phones/${id}`,
                        providesTags: (result, error, id) => [{ type: 'Phone', id }],
                }),
                createPhone: builder.mutation({
                        query: (client) => ({
                                url: '/phones',
                                method: 'POST',
                                body: client,
                        }),
                        invalidatesTags: [
                                'Phone', 
                                'ClientToOrder', 
                                { type: 'Client', id: 'LIST' },
                                { type: 'Phone', id: 'LIST' }
                        ],
                }),
                updatePhone: builder.mutation({
                        query: ({ id, ...patch }) => ({
                                url: `/phones/${id}`,
                                method: 'PATCH',
                                body: patch,
                        }),
                        invalidatesTags: (result, error, { id }) => [
                                { type: 'Phone', id },
                                'Phone', 
                                'ClientToOrder', 
                                { type: 'Client', id: 'LIST' },
                                { type: 'Phone', id: 'LIST' }
                        ],
                }),
                deletePhone: builder.mutation({
                        query: (id) => ({
                                url: `/phones/${id}`,
                                method: 'DELETE',
                        }),
                        invalidatesTags: (result, error, id) => [{ type: 'Phone', id }],
                }),
                blockPhone: builder.mutation({
                        query: (payload) => {
                                // Support pour les anciennes et nouvelles signatures
                                const isOldFormat = typeof payload === 'number' || typeof payload === 'string';
                                const phoneId = isOldFormat ? payload : payload.phoneId || payload.id;
                                const body = isOldFormat ? {} : {
                                        reason: payload.reason,
                                        notes: payload.notes
                                };
                                
                                return {
                                        url: `/phones/${phoneId}/block`,
                                        method: 'POST',
                                        body: body
                                };
                        },
                        invalidatesTags: (result, error, payload) => {
                                const phoneId = typeof payload === 'number' ? payload : (payload.phoneId || payload.id);
                                return [
                                        { type: 'Phone', id: phoneId },
                                        { type: 'Phone', id: 'LIST' }
                                ];
                        },
                }),
                unblockPhone: builder.mutation({
                        query: (id) => ({
                                url: `/phones/${id}/unblock`,
                                method: 'POST',
                        }),
                        invalidatesTags: (result, error, id) => [{ type: 'Phone', id }],
                }),
                deactivatePhone: builder.mutation({
                        query: (id) => ({
                                url: `/phones/${id}/deactivate`,
                                method: 'POST',
                        }),
                        invalidatesTags: (result, error, id) => [{ type: 'Phone', id }],
                }),
                getPhoneWithPaymentStatus: builder.query({
                        query: () => '/phones/lines/with-payment-status',
                        providesTags: ['Phone']
                }),
                getPhonesToBlock: builder.query({
                        query: () => '/phones/lines/to-block',
                        providesTags: ['Phone']
                }),
                getPhonesOverdue: builder.query({
                        query: () => '/phones/lines/overdue',
                        providesTags: ['Phone']
                }),
                // ❌ SUPPRIMÉ: Doublon de getPhonesToActivate (voir ligne 212-215 pour la version correcte)
                // Historique des paiements d'une ligne spécifique
                getPhonePaymentHistory: builder.query({
                        query: (phoneId) => `/phones/${phoneId}/payment-history`,
                        providesTags: (result, error, phoneId) => [
                                { type: 'Phone', id: `history-${phoneId}` },
                                { type: 'Phone', id: phoneId }
                        ]
                }),
                // 🆕 Demander un blocage (sans changer le statut)
                requestBlockPhone: builder.mutation({
                        query: (payload) => {
                                const phoneId = payload.phoneId || payload.id;
                                const body = {
                                        reason: payload.reason,
                                        notes: payload.notes,
                                        billing: payload.billing
                                };
                                
                                return {
                                        url: `/phones/${phoneId}/request-block`,
                                        method: 'POST',
                                        body: body
                                };
                        },
                        invalidatesTags: (result, error, payload) => {
                                const phoneId = payload.phoneId || payload.id;
                                return [
                                        { type: 'Phone', id: phoneId },
                                        { type: 'Phone', id: 'LIST' }
                                ];
                        },
                }),
                // 🆕 Confirmer/rejeter une demande de blocage (action superviseur)
                confirmBlockRequest: builder.mutation({
                        query: ({ phoneId, approved }) => ({
                                url: `/phones/${phoneId}/confirm-block`,
                                method: 'POST',
                                body: { approved }
                        }),
                        invalidatesTags: (result, error, { phoneId }) => [
                                { type: 'Phone', id: phoneId },
                                { type: 'Phone', id: 'LIST' }
                        ],
                }),
                // 🆕 Demander l'activation d'une ligne (pause → actif, dette réglée → actif)
                requestActivation: builder.mutation({
                        query: ({ phoneId, reason }) => ({
                                url: `/phones/${phoneId}/request-activation`,
                                method: 'POST',
                                body: { reason }
                        }),
                        invalidatesTags: (result, error, { phoneId }) => [
                                { type: 'Phone', id: phoneId },
                                { type: 'Phone', id: 'LIST' },
                                { type: 'PhoneToActivate', id: 'LIST' } // Invalider aussi l'onglet À ACTIVER
                        ],
                }),
                // 🆕 Confirmer simplement une réactivation (pour pause/impayé réglé)
                confirmReactivation: builder.mutation({
                        query: ({ phoneId }) => ({
                                url: `/phones/${phoneId}/confirm-reactivation`,
                                method: 'POST'
                        }),
                        invalidatesTags: (result, error, { phoneId }) => [
                                { type: 'Phone', id: phoneId },
                                { type: 'Phone', id: 'LIST' },
                                { type: 'PhoneToActivate', id: 'LIST' } // Retirer de À ACTIVER
                        ],
                }),
                // 🆕 Nouvelles queries pour la gestion des paiements et blocages
                getPhoneWithPaymentStatus: builder.query({
                        query: () => `/phones/lines/with-payment-status`,
                        providesTags: ['Phone', { type: 'Phone', id: 'LIST' }],
                }),
                getPhonesToBlock: builder.query({
                        query: () => `/phones/lines/to-block`,
                        providesTags: ['Phone', { type: 'Phone', id: 'LIST' }],
                }),
                getPhonesOverdue: builder.query({
                        query: () => `/phones/lines/overdue`,
                        providesTags: ['Phone', { type: 'Phone', id: 'LIST' }],
                }),
                getPhonesToActivate: builder.query({
                        query: () => `/phones/lines/to-activate`,
                        providesTags: ['Phone', { type: 'Phone', id: 'LIST' }, { type: 'PhoneToActivate', id: 'LIST' }],
                }),
                // ✅ NOUVELLE MUTATION: Génération facture prorata + encaissement
                generateProrataInvoice: builder.mutation({
                        query: ({ phoneId, iccid, agencyId }) => ({
                                url: `/phones/${phoneId}/generate-prorata-invoice`,
                                method: 'POST',
                                body: { iccid, agencyId }
                        }),
                        invalidatesTags: (result, error, { phoneId }) => [
                                { type: 'Phone', id: phoneId },
                                { type: 'Phone', id: 'LIST' },
                                { type: 'PhoneToActivate', id: 'LIST' }, // Actualiser À ACTIVER
                                'LinePayment' // Actualiser les paiements
                        ],
                }),
                // ✅ NOUVELLE MUTATION: Agence assigne ICCID (À PAYER → Prêt à activer)
                assignIccidToLine: builder.mutation({
                        query: ({ phoneId, iccid }) => ({
                                url: `/phones/${phoneId}/assign-iccid`,
                                method: 'POST',
                                body: { iccid }
                        }),
                        invalidatesTags: (result, error, { phoneId }) => [
                                { type: 'Phone', id: phoneId },
                                { type: 'Phone', id: 'LIST' },
                                { type: 'PhoneToActivate', id: 'LIST' }, // Actualiser À ACTIVER
                                'SimCard', // Actualiser le stock SIM
                                'Agency' // Actualiser les données agence
                        ],
                }),
                // ✅ ANCIENNE MUTATION: Activation directe avec SIM la plus ancienne (compatibilité)
                activateWithOldestSim: builder.mutation({
                        query: ({ phoneId, agencyId }) => ({
                                url: `/phones/${phoneId}/activate-with-oldest-sim`,
                                method: 'POST',
                                body: { agencyId }
                        }),
                        invalidatesTags: (result, error, { phoneId }) => [
                                { type: 'Phone', id: phoneId },
                                { type: 'Phone', id: 'LIST' },
                                { type: 'PhoneToActivate', id: 'LIST' }, // Retirer de À ACTIVER
                                'SimCard', // Actualiser le stock SIM
                                'Agency' // Actualiser les données agence
                        ],
                }),
        }),
});

export const {
        useGetPhonesQuery,
        useGetPhoneByIdQuery,
        useCreatePhoneMutation,
        useUpdatePhoneMutation,
        useDeletePhoneMutation,
        useBlockPhoneMutation,
        useUnblockPhoneMutation,
        useDeactivatePhoneMutation,
        useGetPhoneWithPaymentStatusQuery,
        useGetPhonesToBlockQuery,
        useGetPhonesOverdueQuery,
        useGetPhonesToActivateQuery,
        useGetPhonePaymentHistoryQuery,
        useRequestBlockPhoneMutation, // 🆕
        useConfirmBlockRequestMutation, // 🆕
        useRequestActivationMutation, // 🆕
        useConfirmReactivationMutation, // 🆕
        useGenerateProrataInvoiceMutation, // ✅ NOUVELLE MUTATION
        useAssignIccidToLineMutation, // ✅ NOUVELLE MUTATION AGENCE
        useActivateWithOldestSimMutation, // ✅ ANCIENNE MUTATION (compatibilité)
} = phoneApiSlice;

// Slice Redux pour la gestion d'état locale des phone
const initialState = {
        selectedPhone: null,
        selectedStatus: 'ALL',
        searchTerm: '',
        currentTab: 'list',
};

const phoneSlice = createSlice({
        name: 'phone',
        initialState,
        reducers: {
                setSelectedPhone: (state, action) => {
                        state.selectedPhone = action.payload;
                },
                clearSelectedPhone: (state) => {
                        state.selectedPhone = null;
                },
                setSelectedStatus: (state, action) => {
                        state.selectedStatus = action.payload;
                },
                setSearchTerm: (state, action) => {
                        state.searchTerm = action.payload;
                },
                setCurrentTab: (state, action) => {
                        state.currentTab = action.payload;
                },
        },
});

export const {
        setSelectedPhone,
        clearSelectedPhone,
        setSelectedStatus,
        setSearchTerm,
        setCurrentTab,
} = phoneSlice.actions;

export default phoneSlice.reducer;